/**
 * Unified per-check parser for audit module details.
 *
 * PowerShell audit modules produce details in 4 patterns:
 *   1. failures[] + warnings[]  (AuditPolicy, CredentialProtection, NetworkSecurity, etc.)
 *   2. issues[] + issueCount    (AppControl, BrowserSecurity, TlsCrypto, etc.)
 *   3. Summary string only      (AntiMalware, BitLocker, Firewall, etc. — v2 modules
 *      that also include structured arrays like profiles[], volumes[], adminList[])
 *   4. Custom (runningDangerousServices[])  — ServiceHardening
 *
 * This parser normalises all shapes into a flat Check[] so the UI can render
 * a consistent per-check table regardless of module type.
 */

export type Provenance = 'observed' | 'inferred' | 'missing';

export interface Check {
  name: string;
  status: 'pass' | 'warn' | 'fail' | 'info' | 'unassessed';
  value: string;
  description?: string;
  severity?: 'critical' | 'high' | 'medium' | 'low';
  // PR-1033: structured checks keep their identity and evidence fields so a row
  // can say whether its value was read or assumed, and open its receipt.
  id?: string;
  required?: string;
  provenance?: Provenance;
  basis?: string;
}

/**
 * The label for a check's value. "Observed" only when the agent says it read the
 * value; an inferred value (for example an OS default for an absent registry
 * value) must never be presented as an observation.
 */
export function valueLabel(check: Check): 'Observed' | 'Inferred' | 'Not collected' | 'Reported' {
  if (check.provenance === 'observed') return 'Observed';
  if (check.provenance === 'inferred') return 'Inferred';
  if (check.provenance === 'missing') return 'Not collected';
  return 'Reported';
}

function structuredStatus(status: unknown, provenance: Provenance | undefined): Check['status'] {
  if (provenance === 'missing') return 'unassessed';
  const s = typeof status === 'string' ? status.toLowerCase() : '';
  if (s === 'pass' || s === 'fail' || s === 'info') return s;
  if (s === 'warn' || s === 'warning') return 'warn';
  if (s === 'unassessed' || s === 'not_determined' || s === 'notdetermined') return 'unassessed';
  return 'info';
}

function asProvenance(value: unknown): Provenance | undefined {
  return value === 'observed' || value === 'inferred' || value === 'missing' ? value : undefined;
}

/** Keys that are meta / non-check data and should not be turned into checks. */
const META_KEYS = new Set([
  'score', 'status', 'nodeId', 'name', 'errorMessage', 'metadata',
  'findings', 'issueCount', 'failureCount', 'warningCount',
  'checks', 'schemaVersion',
]);

/** Known array fields whose items should be expanded into individual checks. */
const STRUCTURED_ARRAYS: Record<string, (item: any) => Check> = {
  profiles: (item) => ({
    name: typeof item === 'string' ? item : (item.name || item.profileName || 'Profile'),
    status: profileStatus(item),
    value: typeof item === 'string' ? item : profileValue(item),
    description: item.description,
  }),
  volumes: (item) => ({
    name: typeof item === 'string' ? item : (item.mountPoint || item.driveLetter || item.volumeId || 'Volume'),
    status: volumeStatus(item),
    value: typeof item === 'string' ? item : (item.protectionStatus || item.encryptionStatus || summariseObject(item)),
    description: item.encryptionMethod,
  }),
  adminList: (item) => ({
    name: typeof item === 'string' ? item : (item.name || item.username || 'Admin'),
    status: 'info',
    value: typeof item === 'string' ? item : (item.source || item.type || summariseObject(item)),
  }),
  privileges: (item) => ({
    name: typeof item === 'string' ? item : (item.privilege || item.name || 'Privilege'),
    status: itemStatus(item),
    value: typeof item === 'string' ? item : (item.assignedTo || item.setting || summariseObject(item)),
    description: item.description,
  }),
  protocols: (item) => ({
    name: typeof item === 'string' ? item : (item.protocol || item.name || 'Protocol'),
    status: itemStatus(item),
    value: typeof item === 'string' ? item : (
      item.enabled !== undefined ? (item.enabled ? 'Enabled' : 'Disabled') :
      item.configured !== undefined ? (String(item.configured) === 'true' ? 'Configured' : 'Not configured') :
      summariseObject(item)
    ),
  }),
  services: (item) => ({
    name: typeof item === 'string' ? item : (item.name || item.serviceName || item.displayName || 'Service'),
    status: itemStatus(item),
    value: typeof item === 'string' ? item : (item.status || item.state || item.startType || summariseObject(item)),
  }),
  runningDangerousServices: (item) => ({
    name: typeof item === 'string' ? item : (item.displayName || item.name || item.serviceName || 'Service'),
    status: 'fail',
    value: typeof item === 'string' ? item : (item.status || item.state || summariseObject(item)),
    description: 'Dangerous service is running',
  }),
  stoppedDangerousServices: (item) => ({
    name: typeof item === 'string' ? item : (item.displayName || item.name || item.serviceName || 'Service'),
    status: 'pass',
    value: typeof item === 'string' ? 'Stopped' : (item.status || item.state || summariseObject(item)),
  }),
  notInstalledServices: (item) => ({
    name: typeof item === 'string' ? item : (item.displayName || item.name || 'Service'),
    status: 'pass',
    value: 'Not installed',
  }),
  ciphers: (item) => ({
    name: typeof item === 'string' ? item : (item.name || item.cipher || 'Cipher'),
    status: itemStatus(item),
    value: typeof item === 'string' ? item : (
      item.enabled !== undefined ? (item.enabled ? 'Enabled' : 'Disabled') :
      item.disabled !== undefined ? (String(item.disabled) === 'true' ? 'Disabled' : 'Enabled') :
      summariseObject(item)
    ),
  }),
  extensions: (item) => ({
    name: typeof item === 'string' ? item : (item.name || item.id || 'Extension'),
    status: itemStatus(item),
    value: typeof item === 'string' ? item : (item.status || summariseObject(item)),
  }),
  rules: (item) => ({
    name: typeof item === 'string' ? item : (item.name || item.displayName || 'Rule'),
    status: itemStatus(item),
    value: typeof item === 'string' ? item : (item.action || item.direction || summariseObject(item)),
  }),
};

/**
 * PowerShell modules pack fact + requirement into one sentence, e.g.
 * "Process Creation is not audited (CIS requires Success)". Mapping that whole
 * string into BOTH name and value made every check render its sentence twice.
 * Split the trailing requirement clause into `description`, keep the fact as
 * `name`, and never echo the name into `value`.
 */
function checkFromString(text: string, status: Check['status']): Check {
  const m = text.match(/^(.*\S)\s*\(((?:CIS\s+)?(?:requires?|recommends?|should\s+be)\b[^)]*)\)$/i);
  if (m) {
    return { name: m[1], status, value: '', description: m[2] };
  }
  return { name: text, status, value: '' };
}

/** Object-shaped items: keep fields, but never repeat the name as the value. */
function dedupeValue(check: Check): Check {
  return check.value === check.name ? { ...check, value: '' } : check;
}

/**
 * Parse a module's `details` object into a normalised array of checks.
 */
export function parseModuleChecks(details: Record<string, any> | null | undefined): Check[] {
  if (!details || typeof details !== 'object') return [];

  // --- Structured checks[] (PR-238 dual-emit path) ---
  // When a module emits the standardized checks[] array, use it directly.
  // Falls through to legacy heuristics for old agents / historical rows.
  if (Array.isArray(details.checks) && details.checks.length > 0) {
    const valid = details.checks.every(
      (c: any) => c && typeof c === 'object' && typeof c.name === 'string' && typeof c.status === 'string'
    );
    if (valid) {
      return details.checks.map((c: any) => {
        const provenance = asProvenance(c.provenance);
        const check: Check = {
          name: c.name,
          status: structuredStatus(c.status, provenance),
          value: c.current === undefined || c.current === null ? '' : String(c.current),
          description: [c.required, c.description].filter(Boolean).join(' · ') || undefined,
          severity: (['critical', 'high', 'medium', 'low'].includes(c.severity) ? c.severity : undefined) as Check['severity'],
        };
        if (typeof c.id === 'string') check.id = c.id;
        if (c.required !== undefined && c.required !== null) check.required = String(c.required);
        if (provenance) check.provenance = provenance;
        if (typeof c.basis === 'string') check.basis = c.basis;
        return check;
      });
    }
  }

  const checks: Check[] = [];

  // --- Pattern 1: failures[] + warnings[] ---
  if (Array.isArray(details.failures)) {
    for (const f of details.failures) {
      checks.push(typeof f === 'string' ? checkFromString(f, 'fail') : dedupeValue({
        name: f.name || f.check || summariseObject(f),
        status: 'fail',
        value: f.message || f.detail || summariseObject(f),
        description: f.description,
      }));
    }
  }
  if (Array.isArray(details.warnings)) {
    for (const w of details.warnings) {
      checks.push(typeof w === 'string' ? checkFromString(w, 'warn') : dedupeValue({
        name: w.name || w.check || summariseObject(w),
        status: 'warn',
        value: w.message || w.detail || summariseObject(w),
        description: w.description,
      }));
    }
  }
  // If failures/warnings existed we already handled them; also check for
  // companion "passes" array some modules emit
  if (Array.isArray(details.passes)) {
    for (const p of details.passes) {
      checks.push(typeof p === 'string' ? checkFromString(p, 'pass') : dedupeValue({
        name: p.name || p.check || summariseObject(p),
        status: 'pass',
        value: p.message || p.detail || summariseObject(p),
      }));
    }
  }

  if (checks.length > 0 && !hasStructuredArrays(details)) {
    return checks;
  }

  // --- Pattern 2: issues[] + issueCount ---
  if (Array.isArray(details.issues)) {
    for (const issue of details.issues) {
      checks.push(typeof issue === 'string' ? checkFromString(issue, 'fail') : dedupeValue({
        name: issue.name || issue.check || issue.title || summariseObject(issue),
        status: 'fail',
        value: issue.message || issue.detail || issue.description || summariseObject(issue),
        description: issue.recommendation,
      }));
    }
    if (checks.length > 0) {
      return checks;
    }
  }

  // --- Pattern 3 & 4: Structured arrays (profiles, volumes, adminList, services, etc.) ---
  for (const [key, parser] of Object.entries(STRUCTURED_ARRAYS)) {
    if (Array.isArray(details[key])) {
      for (const item of details[key]) {
        checks.push(parser(item));
      }
    }
  }

  // --- Pattern 3 continued: summary string ---
  if (typeof details.summary === 'string' && details.summary.length > 0) {
    checks.push({
      name: 'Summary',
      status: 'info',
      value: details.summary,
    });
  }

  // --- Boolean key-value pairs (defenderInstalled, smbv1Enabled, etc.) ---
  for (const [key, val] of Object.entries(details)) {
    if (META_KEYS.has(key)) continue;
    if (Array.isArray(val)) continue; // already handled above
    if (val !== null && typeof val === 'object') continue; // skip nested objects

    if (typeof val === 'boolean') {
      checks.push({
        name: formatKeyName(key),
        status: boolToStatus(key, val),
        value: val ? 'Yes' : 'No',
      });
    } else if (typeof val === 'number') {
      checks.push({
        name: formatKeyName(key),
        status: 'info',
        value: String(val),
      });
    } else if (typeof val === 'string' && key !== 'summary') {
      checks.push({
        name: formatKeyName(key),
        status: 'info',
        value: val,
      });
    }
  }

  // Meta-only details (e.g. a module_scores row like {score, status, findings}):
  // a readable one-liner beats a raw JSON dump
  if (checks.length === 0 && (typeof details.score === 'number' || typeof details.findings === 'number')) {
    const bits: string[] = [];
    if (typeof details.score === 'number') bits.push(`score ${details.score}`);
    if (typeof details.findings === 'number') bits.push(`${details.findings} finding${details.findings === 1 ? '' : 's'}`);
    checks.push({
      name: 'Module result',
      status: 'info',
      value: bits.join(' · '),
      description: 'Full per-check detail arrives with the next completed audit',
    });
  }

  // If nothing was parsed, produce a single raw-data check so we never return empty
  // for a non-empty details object
  if (checks.length === 0 && Object.keys(details).length > 0) {
    checks.push({
      name: 'Raw Data',
      status: 'info',
      value: summariseObject(details),
    });
  }

  return checks;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function volumeStatus(item: any): Check['status'] {
  if (typeof item !== 'object' || item === null) return 'info';
  const ps = (item.protectionStatus || '').toString().toLowerCase();
  if (ps === 'on') return 'pass';
  if (ps === 'off') return 'fail';
  const es = (item.encryptionStatus || '').toString().toLowerCase();
  if (es === 'fullyencrypted') return 'pass';
  if (es === 'fullydecrypted' || es === 'none') return 'fail';
  return itemStatus(item);
}

function profileStatus(item: any): Check['status'] {
  if (typeof item !== 'object' || item === null) return 'info';
  if (item.enabled === false) return 'fail';
  if (item.inboundAction && item.inboundAction !== 'Block') return 'warn';
  if (item.enabled === true && (!item.inboundAction || item.inboundAction === 'Block')) return 'pass';
  return itemStatus(item);
}

function profileValue(item: any): string {
  if (typeof item !== 'object' || item === null) return String(item);
  const parts: string[] = [];
  if (typeof item.enabled === 'boolean') parts.push(item.enabled ? 'Enabled' : 'Disabled');
  if (item.inboundAction && parts.length > 0) parts.push(`Inbound: ${item.inboundAction}`);
  return parts.join(', ') || item.status || item.state || item.State || profileEnabledValue(item) || item.inboundAction || summariseObject(item);
}

function profileEnabledValue(item: any): string | null {
  if (item.enabled === true) return 'Enabled';
  if (item.enabled === false) return 'Disabled';
  if (Array.isArray(item.enabled)) return item.enabled.length > 0 ? 'Enabled' : null;
  return null;
}

function hasStructuredArrays(details: Record<string, any>): boolean {
  return Object.keys(STRUCTURED_ARRAYS).some((k) => Array.isArray(details[k]));
}

/** Determine pass/fail from an item object that might have status/enabled/compliant fields. */
function itemStatus(item: any): Check['status'] {
  if (typeof item !== 'object' || item === null) return 'info';
  const s = (item.status || item.state || item.State || '').toString().toLowerCase();
  if (['pass', 'ok', 'compliant', 'enabled', 'running', 'protected', 'on'].includes(s)) return 'pass';
  if (['fail', 'failed', 'non-compliant', 'noncompliant', 'vulnerable', 'off', 'stopped', 'unprotected'].includes(s)) return 'fail';
  if (['warn', 'warning', 'degraded', 'partial'].includes(s)) return 'warn';
  if (item.compliant === true) return 'pass';
  if (item.compliant === false) return 'fail';
  if (item.enabled === true) return 'pass';
  if (item.enabled === false) return 'fail';
  return 'info';
}

/**
 * Convert a boolean details value to a status.
 * Keys ending in "Enabled/Installed/Active/Protected/Configured" with true -> pass.
 * Keys containing "Disabled/Missing/Vulnerable/Expired" with true -> fail (inverted semantics).
 */
function boolToStatus(key: string, val: boolean): Check['status'] {
  const lower = key.toLowerCase();
  // "Negative" keys: true means bad
  const negativePatterns = ['disabled', 'missing', 'vulnerable', 'expired', 'smbv1enabled', 'insecure', 'legacy', 'deprecated'];
  const isNegative = negativePatterns.some((p) => lower.includes(p));
  if (isNegative) return val ? 'fail' : 'pass';
  // Default: true is good
  return val ? 'pass' : 'fail';
}

/** camelCase / PascalCase key -> "Camel Case Key" */
function formatKeyName(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Produce a short string summary for an arbitrary object / value. */
function summariseObject(obj: any): string {
  if (obj === null || obj === undefined) return '—';
  if (typeof obj !== 'object') return String(obj);
  try {
    const str = JSON.stringify(obj);
    return str.length > 120 ? str.slice(0, 117) + '...' : str;
  } catch {
    return '[object]';
  }
}
